// SceneryTextures — Set14 P4 §4.9: the bark/leaf TEXTURE REGISTRY for the bake's
// `MeshStandardMaterial` (the §4.5 material swap). ez-tree ships no textures; the bake renders
// bark with color/normal/roughness maps and leaf with the leaf sprite. This module owns:
//
//   - WHERE the textures live (imported as bundled modules — rspack content-hashes them and emits
//     them as `asset/resource`, the §4.9 narrow rule — so cache-busting is automatic);
//   - WHICH set a `barkType`/`leafType` enum selects;
//   - ASYNC READINESS: `ImpostorBaker.bakeCells` is SYNCHRONOUS, and textures load async. The bake
//     stays synchronous; this module exposes `preload(kinds)` → `Promise<void>` and a synchronous
//     `isReady()`. The renderer/preview gate on `isReady()` and re-bake when readiness flips (§4.9).
//     A bake taken BEFORE its textures arrived must NOT be cached as final — so a texture
//     readiness/version TOKEN joins the bake invalidation signature (§4.10) and the untextured
//     bake is replaced, never cached.
//
// COLOUR SPACE — follow ez-tree's own `src/app/textures.js` convention EXACTLY (§4.9 / §8):
//   colour maps are `SRGBColorSpace`; normal/roughness stay LINEAR. Getting this backwards is the
//   classic washed-out/over-dark bug. (`THREE.ColorManagement` is on by default, so
//   `new THREE.Color(hex)` does sRGB→Linear for the tint — correct by construction under the
//   MeshStandardMaterial, and the reason tints go through THREE.Color and never a raw uniform.)
//
// ⚠ This module is imported by the baker (renderer-side) AND the preview baker (DOM sidebar) —
//   it is NOT worker-safe and must never be imported by the TerrainWorker (it uses THREE.Texture,
//   which needs an image loader, and DOM asset URLs). The worker generates pure geometry; the
//   textures are attached renderer-side only.
import * as THREE from "three";
import {hashString32} from "../SceneryHash.js";

// ── Texture asset imports (bundled modules → emitted asset URLs, §4.9) ────────────────────────
// The 11 bark sets (ambientcg 1K-JPG) + 4 leaf sprites. Imported explicitly so rspack sees every
// file (a dynamic map would defeat the static-analysis rule). `asset/resource` emits each as a
// hashed file; the URL is what THREE.TextureLoader loads.
//
// ⚠ LOCATION: the image FILES live under `Pkg/client/Resources_Bundle/Images/Scenery/{Bark,Leaves}`
// (NOT `Source/` — the client convention keeps only code in `Source/`; assets go in `Resources`
// for raw copy or `Resources_Bundle` for content-hashed bundling). The `Bark/` subfolder holds the
// FLATTENED ambientcg files (`<Set>_Color.jpg` / `_NormalGL.jpg` / `_Roughness.jpg`), and those
// files are Git LFS-managed via `Pkg/client/.gitattributes`.
import bark001Color from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark001_1K-JPG_Color.jpg";
import bark001Normal from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark001_1K-JPG_NormalGL.jpg";
import bark001Roughness from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark001_1K-JPG_Roughness.jpg";
import bark002Color from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark002_1K-JPG_Color.jpg";
import bark002Normal from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark002_1K-JPG_NormalGL.jpg";
import bark002Roughness from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark002_1K-JPG_Roughness.jpg";
import bark003Color from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark003_1K-JPG_Color.jpg";
import bark003Normal from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark003_1K-JPG_NormalGL.jpg";
import bark003Roughness from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark003_1K-JPG_Roughness.jpg";
import bark004Color from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark004_1K-JPG_Color.jpg";
import bark004Normal from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark004_1K-JPG_NormalGL.jpg";
import bark004Roughness from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark004_1K-JPG_Roughness.jpg";
import bark006Color from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark006_1K-JPG_Color.jpg";
import bark006Normal from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark006_1K-JPG_NormalGL.jpg";
import bark006Roughness from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark006_1K-JPG_Roughness.jpg";
import bark007Color from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark007_1K-JPG_Color.jpg";
import bark007Normal from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark007_1K-JPG_NormalGL.jpg";
import bark007Roughness from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark007_1K-JPG_Roughness.jpg";
import bark008Color from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark008_1K-JPG_Color.jpg";
import bark008Normal from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark008_1K-JPG_NormalGL.jpg";
import bark008Roughness from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark008_1K-JPG_Roughness.jpg";
import bark012Color from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark012_1K-JPG_Color.jpg";
import bark012Normal from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark012_1K-JPG_NormalGL.jpg";
import bark012Roughness from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark012_1K-JPG_Roughness.jpg";
import bark013Color from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark013_1K-JPG_Color.jpg";
import bark013Normal from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark013_1K-JPG_NormalGL.jpg";
import bark013Roughness from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark013_1K-JPG_Roughness.jpg";
import bark014Color from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark014_1K-JPG_Color.jpg";
import bark014Normal from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark014_1K-JPG_NormalGL.jpg";
import bark014Roughness from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark014_1K-JPG_Roughness.jpg";
import bark015Color from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark015_1K-JPG_Color.jpg";
import bark015Normal from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark015_1K-JPG_NormalGL.jpg";
import bark015Roughness from "../../../../Resources_Bundle/Images/Scenery/Bark/Bark015_1K-JPG_Roughness.jpg";
import leafAsh from "../../../../Resources_Bundle/Images/Scenery/Leaves/ash.png";
import leafAspen from "../../../../Resources_Bundle/Images/Scenery/Leaves/aspen.png";
import leafOak from "../../../../Resources_Bundle/Images/Scenery/Leaves/oak.png";
import leafPine from "../../../../Resources_Bundle/Images/Scenery/Leaves/pine.png";

/** A bark type id (the `barkType` enum's STORED value) — matches ez-tree's `BarkType` ids. */
export type BarkTypeID = string;
/** A leaf type id (the `leafType` enum's STORED value) — matches ez-tree's `LeafType` ids. */
export type LeafTypeID = string;

/** One bark texture set (the `TreeOptionsBark.maps` slots we ship). */
export interface BarkTextureSet {
	/** The url of a bark set's colour map (sRGB) — the `maps.color` slot. */
	colorUrl: string;
	/** The normal map url (linear) — `maps.normal`. */
	normalUrl: string;
	/** The roughness map url (linear) — `maps.roughness`. */
	roughnessUrl: string;
}

/** The full catalogue of bark sets, keyed by bark type id. All 11 shipped (§4.9). */
export const BARK_TEXTURE_SETS: Readonly<Record<string, BarkTextureSet>> = {
	Bark001: {colorUrl: bark001Color, normalUrl: bark001Normal, roughnessUrl: bark001Roughness},
	Bark002: {colorUrl: bark002Color, normalUrl: bark002Normal, roughnessUrl: bark002Roughness},
	Bark003: {colorUrl: bark003Color, normalUrl: bark003Normal, roughnessUrl: bark003Roughness},
	Bark004: {colorUrl: bark004Color, normalUrl: bark004Normal, roughnessUrl: bark004Roughness},
	Bark006: {colorUrl: bark006Color, normalUrl: bark006Normal, roughnessUrl: bark006Roughness},
	Bark007: {colorUrl: bark007Color, normalUrl: bark007Normal, roughnessUrl: bark007Roughness},
	Bark008: {colorUrl: bark008Color, normalUrl: bark008Normal, roughnessUrl: bark008Roughness},
	Bark012: {colorUrl: bark012Color, normalUrl: bark012Normal, roughnessUrl: bark012Roughness},
	Bark013: {colorUrl: bark013Color, normalUrl: bark013Normal, roughnessUrl: bark013Roughness},
	Bark014: {colorUrl: bark014Color, normalUrl: bark014Normal, roughnessUrl: bark014Roughness},
	Bark015: {colorUrl: bark015Color, normalUrl: bark015Normal, roughnessUrl: bark015Roughness},
};

/** The leaf sprite urls, keyed by leaf type id. All 4 shipped (§4.9). */
export const LEAF_TEXTURE_URLS: Readonly<Record<string, string>> = {
	ash: leafAsh,
	aspen: leafAspen,
	oak: leafOak,
	pine: leafPine,
};

/** The supported leaf-type ids — for the `leafType` enum's `options` (display order). */
export const LEAF_TYPE_IDS: readonly string[] = Object.keys(LEAF_TEXTURE_URLS);

// ── Runtime texture cache + async readiness ──────────────────────────────────────────────────
// The bake's `MeshStandardMaterial` consumes THREE.Texture objects (set on `options.bark.maps` /
// `options.leaves.map`), which THREE.TextureLoader builds asynchronously. Cached by set/type so
// a re-bake reuses the same Texture (and re-preload after a failed load is cheap). A missing file
// must DROP the map (not cache a broken texture) — ez-tree's own `onError` pattern (§4.9).

const textureLoader = new THREE.TextureLoader();
/** Cached THREE.Texture per bark type id (the `maps` object). `null` entry = that set failed. */
const barkCache = new Map<string, {color: THREE.Texture|null; normal: THREE.Texture|null; roughness: THREE.Texture|null}>();
/** Cached THREE.Texture per leaf type id. `null` entry = that sprite failed. */
const leafCache = new Map<string, THREE.Texture|null>();

/** Load a colour map (sRGB — the §4.9/§8 convention). Returns null on failure (degrade). */
function loadColor(url: string): Promise<THREE.Texture|null> {
	return new Promise(resolve=>{
		const t = textureLoader.load(
			url,
			()=>{ t.colorSpace = THREE.SRGBColorSpace; resolve(t); },
			undefined,
			()=>{ console.warn(`[SceneryTextures] missing colour texture: ${url}; falling back to tint.`); resolve(null); },
		);
	});
}

/** Load a linear map (normal/roughness — stays LINEAR per §4.9). Returns null on failure. */
function loadLinear(url: string): Promise<THREE.Texture|null> {
	return new Promise(resolve=>{
		const t = textureLoader.load(url, ()=>resolve(t), undefined,
			()=>{ console.warn(`[SceneryTextures] missing linear texture: ${url}; falling back to tint.`); resolve(null); });
	});
}

/** Preload one bark set into the cache. Idempotent (a cached set is skipped). Resolves when the
 *  set's three maps have settled (each individually either loaded or failed). */
async function preloadBarkSet(type: string): Promise<void> {
	if (barkCache.has(type)) return;
	const set = BARK_TEXTURE_SETS[type];
	if (!set) { barkCache.set(type, {color: null, normal: null, roughness: null}); return; }
	const [color, normal, roughness] = await Promise.all([
		loadColor(set.colorUrl), loadLinear(set.normalUrl), loadLinear(set.roughnessUrl),
	]);
	barkCache.set(type, {color, normal, roughness});
}

/** Preload one leaf sprite into the cache. Idempotent. */
async function preloadLeafSet(type: string): Promise<void> {
	if (leafCache.has(type)) return;
	const url = LEAF_TEXTURE_URLS[type];
	if (!url) { leafCache.set(type, null); return; }
	const tex = await loadColor(url);
	if (tex) {
		tex.premultiplyAlpha = true; // ez-tree's textures.js convention (the leaf sprite has alpha)
		leafCache.set(type, tex);
	} else {
		leafCache.set(type, null);
	}
}

// ── Public API ───────────────────────────────────────────────────────────────────────────────

/** The texture-type ids the bake/preview may need (the union of shipped bark + leaf ids).
 *  `preload` reads a kind's resolved params' `barkType`/`leafType` values; this lists every id
 *  that COULD be requested (used by the preview/renderer to preload ahead of the first bake). */
export function allTextureTypeIDs(): {barkTypes: string[]; leafTypes: string[]} {
	return {barkTypes: Object.keys(BARK_TEXTURE_SETS), leafTypes: LEAF_TYPE_IDS as unknown as string[]};
}

/** Preload the texture sets needed by the given bark/leaf type ids. Resolves when those sets have
 *  settled (loaded or failed). Idempotent. Call before the first bake whose kinds use those ids.
 *  @param barkTypes bark type ids (e.g. `["Bark003"]`), @param leafTypes leaf type ids (e.g. `["pine"]`). */
export async function preloadTextures(barkTypes: readonly string[], leafTypes: readonly string[]): Promise<void> {
	await Promise.all([
		...barkTypes.map(preloadBarkSet),
		...leafTypes.map(preloadLeafSet),
	]);
}

/** Synchronous readiness check: are all the given type ids' textures cached (loaded OR failed)?
 *  The bake gates on this so a tree baked BEFORE its textures arrived is replaced, not cached (§4.9). */
export function texturesReady(barkTypes: readonly string[], leafTypes: readonly string[]): boolean {
	for (const t of barkTypes) if (!barkCache.has(t)) return false;
	for (const t of leafTypes) if (!leafCache.has(t)) return false;
	return true;
}

/** The texture-readiness/version TOKEN (§4.10) — joins the bake invalidation signature. When the
 *  set of textures a bake depends on changes identity (a set re-preloaded, e.g. after a failed
 *  load recovers, or a type id resolved to a different texture), the token changes → the prior
 *  bake is replaced. Monotonic-ish: derived from the cached texture ids + their on-disk urls
 *  (content-hashed by rspack, so a re-emitted asset changes it too). Deterministic for a given
 *  cache state. */
export function textureVersionToken(barkTypes: readonly string[], leafTypes: readonly string[]): string {
	const parts: string[] = [];
	for (const t of barkTypes) {
		const set = barkCache.get(t);
		parts.push(`b:${t}:${set ? (set.color ? "c" : "0") : "?"}${set ? (set.normal ? "n" : "0") : "?"}${set ? (set.roughness ? "r" : "0") : "?"}`);
	}
	for (const t of leafTypes) {
		const tex = leafCache.get(t);
		parts.push(`l:${t}:${tex ? "1" : "0"}`);
	}
	return (hashString32(parts.join("|")) >>> 0).toString(16);
}

/** Look up a bark set's cached THREE.Texture maps (for attaching to `options.bark.maps`). Returns
 *  null if the set isn't preloaded (callers gate on `texturesReady` first). A null slot = failed
 *  load → the material falls back to the tint for that channel (§4.9 degrade). */
export function getBarkTextures(type: string): {color: THREE.Texture|null; normal: THREE.Texture|null; roughness: THREE.Texture|null} | null {
	return barkCache.get(type) ?? null;
}

/** Look up a leaf sprite's cached THREE.Texture (for `options.leaves.map`). Null if not preloaded
 *  or failed → leaves render as flat tinted quads (§4.9 degrade). */
export function getLeafTexture(type: string): THREE.Texture | null {
	return leafCache.get(type) ?? null;
}

// ── TEST-ONLY injection (Set14 P6 §7) ─────────────────────────────────────────────────────────
// The texture *files* can't load in the vitest browser lane (the `asset/resource` URLs from rspack
// aren't served), so the §7 GPU assertion "a TEXTURED bake is non-trivially different from an
// UNTEXTURED bake" cannot use the real files. Instead, a test injects a KNOWN synthetic texture
// into the cache (a solid-colour DataTexture), asserts the bake with it differs measurably from
// the untextured tint-fallback bake, then removes it. This proves the texture path actually
// reaches the atlas — otherwise a silently-unbound map looks like "the tree is a bit flat"
// (§7's exact concern). NO production path calls these (they're `__testOnly`).

/** Remove a bark set + leaf type from the cache (restores the pristine not-ready state). */
export function __testOnly_clearTextures(barkTypes: readonly string[], leafTypes: readonly string[]): void {
	for (const t of barkTypes) barkCache.delete(t);
	for (const t of leafTypes) leafCache.delete(t);
}

/** Inject a synthetic bark set into the cache (a solid-colour colour map + linear normal/roughness),
 *  so a bake can exercise the TEXTURED material path without served assets. Returns the injected
 *  set (already in the cache). */
export function __testOnly_injectBarkTextures(type: string, color: [number, number, number]): {color: THREE.Texture; normal: THREE.Texture; roughness: THREE.Texture} {
	const make = (rgb: [number, number, number], colorSpace: THREE.ColorSpace): THREE.Texture => {
		const data = new Uint8Array([rgb[0], rgb[1], rgb[2], 255]);
		const tex = new THREE.DataTexture(data, 1, 1, THREE.RGBAFormat);
		tex.colorSpace = colorSpace;
		tex.needsUpdate = true;
		return tex;
	};
	const set = {
		color: make(color, THREE.SRGBColorSpace),
		normal: make([128, 128, 255], THREE.LinearSRGBColorSpace),
		roughness: make([128, 128, 128], THREE.LinearSRGBColorSpace),
	};
	barkCache.set(type, set);
	return set;
}

/** Inject a synthetic leaf sprite into the cache (a 1-px opaque DataTexture). Returns the injected
 *  texture. */
export function __testOnly_injectLeafTexture(type: string, color: [number, number, number]): THREE.Texture {
	const data = new Uint8Array([color[0], color[1], color[2], 255]);
	const tex = new THREE.DataTexture(data, 1, 1, THREE.RGBAFormat);
	tex.colorSpace = THREE.SRGBColorSpace;
	tex.needsUpdate = true;
	leafCache.set(type, tex);
	return tex;
}
